import {useEffect, useState} from 'react'
import Typography from '@mui/material/Typography'
import {useNavigate} from 'react-router-dom'
import {
  StyledBreadcrumbPopover,
  StyledBreadcrumbPopoverContent,
  StyledBreadcrumbs,
  StyledLink
} from './BreadcrumbStyles'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import PropTypes from 'prop-types'
import {Button} from '@mui/material'

function generateAriaLabel(link) {
  return `${link}, link list item`
}

const BREADCRUMB_MAX_CHARACTERS = 20

function getTruncatedBreadcrumbLabel(label = '') {
  if (label.length <= BREADCRUMB_MAX_CHARACTERS) {
    return {displayLabel: label, isTruncated: false}
  }

  return {
    displayLabel: `${label.slice(0, BREADCRUMB_MAX_CHARACTERS)}...`,
    isTruncated: true
  }
}

const BreadcrumbComponent = ({
  props,
  goBack,
  setOpen,
  setPopupStyle,
  isProjectView,
  showPopup,
  create,
  hasProjects,
  isClient,
  inCustomSettings = false,
  hideBackButton = false
}) => {
  const [breadcrumbId, setBreadcrumbId] = useState(0)
  const [breadcrumbClicked, setBreadcrumbClicked] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [popoverText, setPopoverText] = useState('')

  const navigateBack = () => {
    if ((create && !hasProjects) || !create) initialBreadcrumbRoute.pop()
    navigate(initialBreadcrumbRoute.at(-1))
  }
  const gotoBreadcrumbId = (index) => {
    navigate(initialBreadcrumbRoute[index + 1])
  }
  useEffect(() => {
    if (goBack) {
      breadcrumbClicked ? gotoBreadcrumbId(breadcrumbId) : navigateBack()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goBack, breadcrumbId, breadcrumbClicked])

  const handlePopup = (popUp) => {
    if (popUp) {
      setOpen(true)
      if (!inCustomSettings) setPopupStyle('back')
    } else {
      navigateBack()
    }
  }

  const handleClientBack = () => {
    const popUp = isProjectView && showPopup
    handlePopup(popUp)
  }

  const handleBreadCrumbClick = (index) => {
    if (isClient || (showPopup && inCustomSettings)) {
      handlePopup(showPopup)
    } else if (isProjectView && showPopup) {
      setBreadcrumbId(index)
      setBreadcrumbClicked(true)
      setOpen(true)
      setPopupStyle('back')
    }

    if ((!inCustomSettings && !isClient && !(isProjectView && showPopup)) || (inCustomSettings && !showPopup)) {
      gotoBreadcrumbId(index)
    }
  }

  const onEnterClick = (event) => {
    if (event.key === 'Enter') {
      handleBreadCrumbClick(-1)
    }
  }

  const handleBreadcumbFirstLink = () => {
    handleBreadCrumbClick(-1)
  }

  const handlePopoverOpen = (event, label, isTruncated) => {
    if (!isTruncated) {
      return
    }

    setPopoverText(label)
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const navigate = useNavigate()
  const initialBreadcrumbRoute = ['/adminpanel/']

  function generateAdminstrationAriaLabel() {
    return `Navigation ordered list with ${props?.length + 1} items, Administration, link list item`
  }

  return (
    <div>
      <StyledBreadcrumbPopover
        id='breadcrumb-popover'
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        slotProps={{paper: {role: 'tooltip'}}}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <StyledBreadcrumbPopoverContent>{popoverText}</StyledBreadcrumbPopoverContent>
      </StyledBreadcrumbPopover>

      <StyledBreadcrumbs aria-label='breadcrumb'>
        {props?.length > 0 ? (
          <StyledLink
            tabIndex={0}
            onKeyDown={onEnterClick}
            onClick={handleBreadcumbFirstLink}
            aria-label={generateAdminstrationAriaLabel()}
          >
            Administration
          </StyledLink>
        ) : (
          <Typography tabIndex={0} aria-label={generateAdminstrationAriaLabel()}>
            Administration
          </Typography>
        )}
        {props?.map((obj, index) => {
          const isLastBreadcrumb = index === props?.length - 1
          const {displayLabel, isTruncated} = getTruncatedBreadcrumbLabel(obj.name)
          const updateBreadcrumbRoute = initialBreadcrumbRoute[index] + obj.key + '/' + obj.id + '/'
          initialBreadcrumbRoute.push(updateBreadcrumbRoute)
          return isLastBreadcrumb ? (
            <Typography
              tabIndex={0}
              key={`route-heading-${obj.id}`}
              variant='subtitle1'
              aria-label={generateAriaLabel(obj.name)}
              onMouseEnter={(event) => handlePopoverOpen(event, obj.name, isTruncated)}
              onMouseLeave={handlePopoverClose}
              onFocus={(event) => handlePopoverOpen(event, obj.name, isTruncated)}
              onBlur={handlePopoverClose}
              aria-haspopup={isTruncated ? 'true' : undefined}
              aria-owns={anchorEl ? 'breadcrumb-popover' : undefined}
            >
              {displayLabel}
            </Typography>
          ) : (
            <StyledLink
              aria-label={generateAriaLabel(obj.name)}
              key={`route-link-${obj.id}`}
              tabIndex={index}
              onKeyDown={(e) => e.key === 'Enter' && handleBreadCrumbClick(index)}
              onClick={() => handleBreadCrumbClick(index)}
              onMouseEnter={(event) => handlePopoverOpen(event, obj.name, isTruncated)}
              onMouseLeave={handlePopoverClose}
              onFocus={(event) => handlePopoverOpen(event, obj.name, isTruncated)}
              onBlur={handlePopoverClose}
              aria-haspopup={isTruncated ? 'true' : undefined}
              aria-owns={anchorEl ? 'breadcrumb-popover' : undefined}
            >
              {displayLabel}
            </StyledLink>
          )
        })}
      </StyledBreadcrumbs>

      {!hideBackButton && (
        <Button
          variant='text'
          startIcon={<ChevronLeftIcon />}
          onClick={handleClientBack}
          sx={{marginTop: 1}}
          aria-label='Go back'
        >
          Back
        </Button>
      )}
    </div>
  )
}

BreadcrumbComponent.propTypes = {
  props: PropTypes.array.isRequired,
  goBack: PropTypes.bool,
  setOpen: PropTypes.func,
  setPopupStyle: PropTypes.func,
  isProjectView: PropTypes.bool,
  showPopup: PropTypes.bool,
  create: PropTypes.any,
  hasProjects: PropTypes.bool,
  isClient: PropTypes.bool,
  length: PropTypes.number,
  map: PropTypes.object,
  inCustomSettings: PropTypes.bool,
  hideBackButton: PropTypes.bool
}

export default BreadcrumbComponent
